import React, { useState, useEffect, useRef } from 'react';
import { APP_LOGO_PNG } from '../branding';

/**
 * Sticky header with safe areas; collapsible nav on small screens (no business logic).
 */
export default function AppHeader({
  title,
  displayName,
  onProfileClick,
  onLogout,
  /** When set, logo and title act as a control to return to the app home view. */
  onHomeClick,
  i18n,
  t,
  /** @type {Array<{ label: string; onClick: () => void }>} */
  navItems = [],
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  useEffect(() => {
    setMenuOpen(false);
  }, [navItems.length, title]);

  useEffect(() => {
    if (!userMenuOpen) return;
    const close = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('touchstart', close);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('touchstart', close);
    };
  }, [userMenuOpen]);

  const LangToggle = ({ className = '' }) => (
    <div
      role="group"
      aria-label="Language"
      className={`inline-flex rounded-xl border border-emerald-200/80 overflow-hidden p-0.5 bg-white/90 shrink-0 ${className}`}
    >
      <button
        type="button"
        onClick={() => i18n.changeLanguage('cs')}
        className={`px-2.5 sm:px-3 py-1.5 text-sm font-bold rounded-lg min-h-[40px] min-w-[2.5rem] transition-colors ${
          i18n.language?.startsWith('cs')
            ? 'bg-brand-emerald text-white'
            : 'text-slate-600 hover:bg-emerald-50'
        }`}
      >
        CZ
      </button>
      <button
        type="button"
        onClick={() => i18n.changeLanguage('en')}
        className={`px-2.5 sm:px-3 py-1.5 text-sm font-bold rounded-lg min-h-[40px] min-w-[2.5rem] transition-colors ${
          i18n.language?.startsWith('en')
            ? 'bg-brand-emerald text-white'
            : 'text-slate-600 hover:bg-emerald-50'
        }`}
      >
        EN
      </button>
    </div>
  );

  const NavItemButtons = ({ className = '' }) => (
    <div className={className}>
      {navItems.map((item, i) => (
        <button
          key={`${item.label}-${i}`}
          type="button"
          onClick={() => {
            item.onClick();
            setMenuOpen(false);
          }}
          className="w-full sm:w-auto text-left sm:text-center py-3 px-3 sm:py-2 sm:px-3 rounded-xl text-base font-semibold text-brand-green/95 hover:bg-emerald-50 hover:text-brand-green min-h-[48px] sm:min-h-0"
        >
          {item.label}
        </button>
      ))}
    </div>
  );

  const showName = Boolean(displayName && displayName.trim());

  return (
    <header className="sticky top-0 z-50 border-b border-emerald-200/70 bg-white/90 backdrop-blur-md supports-[backdrop-filter]:bg-white/85 shadow-sm shadow-emerald-900/5">
      <div
        className="max-w-5xl mx-auto w-full px-3 sm:px-5 flex flex-row items-center gap-2 sm:gap-4 min-h-[6.5rem] sm:min-h-[7.25rem] md:min-h-[8rem] py-2 sm:py-2.5 min-w-0"
        style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top))' }}
      >
        {typeof onHomeClick === 'function' ? (
          <button
            type="button"
            onClick={onHomeClick}
            className="flex items-center gap-3 sm:gap-4 min-w-0 flex-1 pr-1 text-left rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/90 focus-visible:ring-offset-2 group"
            aria-label={t('app.goHome')}
          >
            <img
              src={APP_LOGO_PNG}
              alt=""
              className="h-[6.75rem] sm:h-[7.5rem] md:h-[8.25rem] w-auto max-h-[8.25rem] object-contain object-left shrink-0 group-hover:opacity-95 transition-opacity"
              decoding="async"
            />
            <span className="text-lg sm:text-xl md:text-2xl font-sans font-bold text-brand-green tracking-tight leading-tight sm:leading-snug truncate min-w-0 group-hover:underline decoration-brand-green/40 underline-offset-2">
              {title}
            </span>
          </button>
        ) : (
          <div className="flex items-center gap-3 sm:gap-4 min-w-0 flex-1 pr-1">
            <img
              src={APP_LOGO_PNG}
              alt=""
              className="h-[6.75rem] sm:h-[7.5rem] md:h-[8.25rem] w-auto max-h-[8.25rem] object-contain object-left shrink-0"
              decoding="async"
            />
            <h1 className="text-lg sm:text-xl md:text-2xl font-sans font-bold text-brand-green tracking-tight leading-tight sm:leading-snug truncate min-w-0">
              {title}
            </h1>
          </div>
        )}

        <div className="flex flex-row flex-nowrap items-center justify-end gap-x-2 gap-y-1 shrink-0">
          <div className="hidden md:flex flex-row flex-wrap items-center justify-end gap-x-2 gap-y-1 min-w-0">
            <NavItemButtons className="flex flex-row flex-wrap items-center justify-end gap-1" />
            {showName ? (
              <div className="relative pl-1" ref={userMenuRef}>
                <button
                  type="button"
                  onClick={() => setUserMenuOpen((o) => !o)}
                  className="flex items-center gap-1 max-w-[14rem] lg:max-w-[18rem] rounded-xl px-2.5 py-2 text-base font-semibold text-slate-800 hover:bg-emerald-50 border border-transparent hover:border-emerald-200/80 min-h-[44px]"
                  aria-expanded={userMenuOpen}
                  aria-haspopup="true"
                >
                  <span className="truncate min-w-0">{displayName}</span>
                  <svg className="w-4 h-4 shrink-0 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {userMenuOpen ? (
                  <div
                    className="absolute right-0 mt-1 min-w-[12rem] rounded-xl border border-emerald-200/80 bg-white py-1 shadow-lg z-[60]"
                    role="menu"
                  >
                    {typeof onProfileClick === 'function' ? (
                      <button
                        type="button"
                        role="menuitem"
                        className="w-full text-left px-4 py-3 text-base font-semibold text-brand-green hover:bg-emerald-50"
                        onClick={() => {
                          onProfileClick();
                          setUserMenuOpen(false);
                        }}
                      >
                        {t('auth.profile')}
                      </button>
                    ) : null}
                    <button
                      type="button"
                      role="menuitem"
                      className="w-full text-left px-4 py-3 text-base font-semibold text-slate-600 hover:bg-amber-50/80"
                      onClick={() => {
                        onLogout();
                        setUserMenuOpen(false);
                      }}
                    >
                      {t('auth.logout')}
                    </button>
                  </div>
                ) : null}
              </div>
            ) : null}
            <LangToggle />
          </div>
          <button
            type="button"
            className="md:hidden flex items-center justify-center min-w-[44px] min-h-[44px] rounded-xl text-brand-green hover:bg-emerald-50 border border-transparent hover:border-emerald-200/80 shrink-0"
            aria-expanded={menuOpen}
            aria-controls="app-mobile-nav"
            aria-label={menuOpen ? t('app.menuClose') : t('app.menu')}
            onClick={() => setMenuOpen((o) => !o)}
          >
            {menuOpen ? (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path strokeWidth={2} strokeLinecap="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path strokeWidth={2} strokeLinecap="round" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>
      </div>

      {menuOpen && (
        <div
          id="app-mobile-nav"
          className="md:hidden border-t border-emerald-100 bg-emerald-50/40 shadow-inner max-h-[min(70vh,calc(100dvh-4rem))] overflow-y-auto overscroll-contain"
          style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
        >
          <div className="max-w-5xl mx-auto w-full px-3 py-4 flex flex-col gap-4">
            {showName ? (
              <div className="font-semibold text-slate-800 truncate text-base border-b border-emerald-100/80 pb-2">
                {displayName}
              </div>
            ) : null}
            {typeof onProfileClick === 'function' ? (
              <button
                type="button"
                className="w-full text-left py-3 px-3 rounded-xl text-base font-semibold text-brand-green hover:bg-white/80 min-h-[48px]"
                onClick={() => {
                  onProfileClick();
                  setMenuOpen(false);
                }}
              >
                {t('auth.profile')}
              </button>
            ) : null}
            <button
              type="button"
              className="w-full text-left py-3 px-3 rounded-xl text-base font-semibold text-slate-600 hover:bg-white/80 min-h-[48px]"
              onClick={() => {
                onLogout();
                setMenuOpen(false);
              }}
            >
              {t('auth.logout')}
            </button>
            <NavItemButtons className="flex flex-col gap-1 border-t border-emerald-100/80 pt-3" />
            <LangToggle className="self-start" />
          </div>
        </div>
      )}
    </header>
  );
}
